import { DateTimePicker, LocalizationProvider, pickersLayoutClasses } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import { useEffect, useState, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { InputLabel, Select, FormControl, MenuItem } from '@mui/material';
import { getMetricClients, getMetricCountries, getMetricGames } from 'services/metrics';

export default function ChartFilters({ setFilters }) {
  const [from, setFrom] = useState(null); // Dayjs | null
  const [to, setTo] = useState(null); // Dayjs | null
  const [subFilters, setSubFilters] = useState([]);
  const [selectedSubFilter, setSelectedSubFilter] = useState('');

  const loadSubFilters = async () => {
    const res = await getMetricClients();

    if (res?.status === 200 || res?.status === 201) {
      const items = (res?.data ?? []).map((i) => ({
        id: String(i.id),
        name: i.name
      }));

      setSubFilters(items);

      if (!items.some((i) => i.id === selectedSubFilter)) {
        setSelectedSubFilter('');
      }
    } else {
      setSubFilters([]);
      setSelectedSubFilter('');
    }
  };

  useEffect(() => {
    loadSubFilters();
  }, []);

  const onSubFilterChange = (e) => {
    const value = e.target.value;
    setSelectedSubFilter(value);
    setFilters((filters) => ({
      ...filters,
      client: value
    }));
  };

  // === Date range handlers (Dayjs) ===

  const handleFromChange = (date) => {
    if (!date) {
      setFrom(null);
      return;
    }

    if (to && date.isAfter(to)) {
      // swap
      setFrom(to);
      setTo(date);
    } else {
      setFrom(date);
    }
  };

  const handleToChange = (date) => {
    if (!date) {
      setTo(null);
      return;
    }

    if (from && date.isBefore(from)) {
      // swap
      setFrom(date);
      setTo(from);
    } else {
      setTo(date);
    }
  };

  // Push normalized values to parent filters (convert Dayjs -> Date or string)
  useEffect(() => {
    setFilters((filters) => ({
      ...filters,
      // if your API prefers ISO strings, do from?.toISOString() instead
      startDate: from ? from.toDate() : '',
      endDate: to ? to.toDate() : ''
    }));
  }, [from, to, setFilters]);

  return (
    <MDBox sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginLeft: 'auto' }} mb={2}>
      <FormControl sx={{ minWidth: 240 }} disabled={!subFilters.length}>
        <InputLabel id='sub-filter-label'>Integrations</InputLabel>
        <Select
          labelId='sub-filter-label'
          id='sub-filter'
          label='Sub-filter'
          value={selectedSubFilter}
          style={{ padding: '10px' }}
          onChange={onSubFilterChange}
          renderValue={(val) => (val !== '' ? subFilters.find((s) => s?.id === val)?.name : 'None')}
        >
          <MenuItem value=''>
            <em>None</em>
          </MenuItem>

          {subFilters.map((sf, index) => (
            <MenuItem key={sf?.id ?? index} value={sf?.id ?? sf}>
              {sf?.name ?? sf}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          label='From'
          ampmInClock={false}
          closeOnSelect={true}
          ampm={false}
          showDaysOutsideCurrentMonth
          value={from}
          viewRenderers={{
            hours: null,
            minutes: null,
            seconds: null
          }}
          onChange={handleFromChange}
          slotProps={{
            layout: {
              sx: {
                [`.${pickersLayoutClasses.actionBar}`]: {
                  display: 'none'
                }
              }
            }
          }}
        />
        <DateTimePicker
          label='To'
          ampmInClock={false}
          closeOnSelect={true}
          ampm={false}
          showDaysOutsideCurrentMonth
          value={to}
          viewRenderers={{
            hours: null,
            minutes: null,
            seconds: null
          }}
          onChange={handleToChange}
          slotProps={{
            layout: {
              sx: {
                [`.${pickersLayoutClasses.actionBar}`]: {
                  display: 'none'
                }
              }
            }
          }}
        />
      </LocalizationProvider>
    </MDBox>
  );
}
